import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { AuthBrandPanel, BrandMark } from '../../components/AuthBrandPanel';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { AlertTriangle, CheckCircle, Lock } from 'lucide-react';
import { config } from '../../config';

const resetPasswordConfirmSchema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetPasswordConfirmFormData = z.infer<typeof resetPasswordConfirmSchema>;

const ResetPasswordConfirmPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const form = useForm<ResetPasswordConfirmFormData>({
    resolver: zodResolver(resetPasswordConfirmSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const handleResetPassword = async (data: ResetPasswordConfirmFormData) => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/v1/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: data.newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to reset password');
      }

      setIsSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to reset password. The link may have expired.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const content = !token ? (
    <CardContent className="space-y-6 pt-10 pb-8 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100">
        <AlertTriangle className="h-8 w-8 text-amber-600" />
      </div>
      <div className="space-y-2">
        <CardTitle className="text-2xl font-semibold">Invalid reset link</CardTitle>
        <CardDescription className="text-base leading-7">
          This password reset link is invalid or has expired. Please request a new one.
        </CardDescription>
      </div>
      <div className="space-y-3">
        <Button asChild className="w-full" size="lg">
          <Link to="/auth/reset-password">Request new reset link</Link>
        </Button>
        <Button asChild variant="ghost" className="w-full">
          <Link to="/auth">Back to sign in</Link>
        </Button>
      </div>
    </CardContent>
  ) : isSuccess ? (
    <CardContent className="space-y-6 pt-10 pb-8 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
        <CheckCircle className="h-8 w-8 text-primary" />
      </div>
      <div className="space-y-2">
        <CardTitle className="text-2xl font-semibold">Password reset complete</CardTitle>
        <CardDescription className="text-base leading-7">
          Your password has been reset successfully. You can now sign in with your new
          password.
        </CardDescription>
      </div>
      <Button asChild className="w-full" size="lg">
        <Link to="/auth">Sign in</Link>
      </Button>
    </CardContent>
  ) : (
    <>
      <CardHeader className="space-y-3 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
          <Lock className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl font-semibold">Set new password</CardTitle>
        <CardDescription className="mx-auto max-w-sm text-base leading-7">
          Enter your new password below. Make sure it is at least 8 characters long.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleResetPassword)} className="space-y-6">
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter your new password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Confirm your new password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading} size="lg">
              {isLoading ? 'Resetting...' : 'Reset password'}
            </Button>
          </form>
        </Form>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Remember your password?{' '}
            <Link to="/auth" className="font-medium text-primary hover:text-primary/80">
              Sign in
            </Link>
          </p>
        </div>
      </CardContent>
    </>
  );

  return (
    <div className="min-h-screen bg-white lg:flex">
      <AuthBrandPanel />

      <div className="flex-1 bg-figma-bg-main">
        <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-4 py-6 sm:px-6 lg:py-8">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 lg:hidden">
              <BrandMark />
              <div>
                <p className="text-lg font-semibold text-figma-text-primary">Good Outcomes</p>
                <p className="text-sm text-figma-text-secondary">Set new password</p>
              </div>
            </Link>

            <Button
              asChild
              variant="ghost"
              className="ml-auto text-primary hover:bg-primary/5 hover:text-primary"
            >
              <Link to="/auth">Back to sign in</Link>
            </Button>
          </div>

          <div className="my-auto py-8">
            <Card className="border-border bg-card shadow-sm">{content}</Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordConfirmPage;
